import { useState } from "react";
import { doc, updateDoc, arrayUnion, getDoc } from "firebase/firestore";
import { db } from "../../config/firebase";
import "./Button.css";

export const useLinkCreator = (storyId, nodes) => {
  const [linkMode, setLinkMode] = useState(false);
  const [selectedParent, setSelectedParent] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  // 選択された親ノード情報を返すようにする
  const selectedNode = nodes.find((n) => n.id === selectedParent) || null;

  const saveLinkToFirestore = async (parentId, childId) => {
    try {
      const parentRef = doc(db, "stories", storyId, "nodes", parentId);
      const childRef = doc(db, "stories", storyId, "nodes", childId);

      const parentSnap = await getDoc(parentRef);
      const childSnap = await getDoc(childRef);
      if (!parentSnap.exists() || !childSnap.exists()) {
        setErrorMessage("リンク対象のノードがFirestoreに存在しません。");
        return;
      }

      await updateDoc(parentRef, { childrenIds: arrayUnion(childId) });
      await updateDoc(childRef, { parentId: arrayUnion(parentId) });
    } catch (err) {
      setErrorMessage("Firestore保存中にエラーが発生しました。");
    }
  };

  const getDepthFromRoot = (nodeId, visited = new Set()) => {
    if (!nodeId) return Infinity;
    if (visited.has(nodeId)) return Infinity;
    visited.add(nodeId);

    let depth = 0;
    let current = nodes.find((n) => n.id === nodeId);

    while (current && Array.isArray(current.parentId) && current.parentId.length > 0) {
      const nextParentId = current.parentId[0];
      if (visited.has(nextParentId)) return Infinity;
      depth++;
      current = nodes.find((n) => n.id === nextParentId);
    }
    return depth;
  };

  const handleNodeClick = (node) => {
    if (!linkMode) return;

    if (!selectedParent) {
      setSelectedParent(node.id);
      const el = document.querySelector(`[data-nodeid="${node.id}"]`);
      if (el) el.classList.add("highlight-node");
      return;
    }

    if (node.id === selectedParent) {
      setErrorMessage("親ノードと同じノードが選択されました。リンクは作成されません。");
      setSelectedParent(null);
      document.querySelectorAll(".highlight-node").forEach((el) =>
        el.classList.remove("highlight-node")
      );
      setLinkMode(false);
      return;
    }

    try {
      const nodeA = nodes.find((n) => n.id === selectedParent);
      const nodeB = node;

      if (!nodeA || !nodeB) {
        setErrorMessage("選択したノードが見つかりません。");
        return;
      }

      const depthA = getDepthFromRoot(nodeA.id);
      const depthB = getDepthFromRoot(nodeB.id);

      let parentId, childId;
      if (depthA <= depthB) {
        parentId = nodeA.id;
        childId = nodeB.id;
      } else {
        parentId = nodeB.id;
        childId = nodeA.id;
      }

      const isCircular = (startId, targetId, visited = new Set()) => {
        if (visited.has(startId)) return false;
        visited.add(startId);
        const startNode = nodes.find((n) => n.id === startId);
        if (!startNode) return false;

        if (Array.isArray(startNode.childrenIds) && startNode.childrenIds.includes(targetId)) {
          return true;
        }

        return (startNode.childrenIds || []).some((cid) =>
          isCircular(cid, targetId, visited)
        );
      };

      if (isCircular(childId, parentId)) {
        setErrorMessage("循環構造になるためリンクできません。");
        cancelLinkMode();
        return;
      }

      saveLinkToFirestore(parentId, childId);
    } catch (err) {
      setErrorMessage("リンク作成中に予期せぬエラーが発生しました。");
    } finally {
      document.querySelectorAll(".highlight-node").forEach((el) =>
        el.classList.remove("highlight-node")
      );
      setSelectedParent(null);
      setLinkMode(false);
    }
  };

  const toggleLinkMode = () => {
    if (linkMode) cancelLinkMode();
    else startLinkMode();
  };

  const startLinkMode = () => {
    setSelectedParent(null);
    setLinkMode(true);
    setErrorMessage("");
  };

  const cancelLinkMode = () => {
    setSelectedParent(null);
    setLinkMode(false);
    document.querySelectorAll(".highlight-node").forEach((el) =>
      el.classList.remove("highlight-node")
    );
    setErrorMessage("");
  };

  return {
    linkMode,
    selectedParent,
    selectedNode,       // ここを追加
    handleNodeClick,
    toggleLinkMode,
    errorMessage,
  };
};
